/**
 * =====================================================================================
 * CLIENTE HTTP BASE (AXIOS) - api.js
 * =====================================================================================
 *
 * Este archivo configura el cliente HTTP centralizado para toda la aplicación.
 * Utilizamos Axios, una librería popular para hacer peticiones HTTP en JavaScript.
 *
 * ¿Por qué un cliente HTTP centralizado?
 * --------------------------------------
 * En lugar de configurar Axios en cada componente o servicio, creamos una instancia
 * única con configuración base (URL, headers, timeout). Esto nos da:
 *
 * 1. CONSISTENCIA: Todas las peticiones usan la misma configuración
 * 2. MANTENIBILIDAD: Cambios en un solo lugar afectan toda la app
 * 3. INTERCEPTORES: Podemos agregar lógica automática antes/después de cada petición
 * 4. AUTENTICACIÓN: El token JWT se agrega automáticamente a todas las peticiones
 *
 * FLUJO DE UNA PETICIÓN HTTP:
 * ---------------------------
 *
 *   Componente/Servicio llama api.get('/facturas')
 *                          ↓
 *   [INTERCEPTOR REQUEST] → Agrega token JWT al header Authorization
 *                          ↓
 *   Petición sale hacia → https://localhost:7039/api/facturas
 *                          ↓
 *   Backend procesa y responde
 *                          ↓
 *   [INTERCEPTOR RESPONSE] → Verifica si hay errores (401, 403, etc.)
 *                          ↓
 *   Componente/Servicio recibe los datos
 *
 * RELACIÓN CON OTROS ARCHIVOS:
 * ----------------------------
 * - Este archivo es IMPORTADO por todos los servicios (auth.service.js, facturas.service.js, etc.)
 * - Los servicios usan: api.get(), api.post(), api.put(), api.delete()
 * - El token JWT se obtiene de localStorage (guardado por auth.service.js después del login)
 *
 * =====================================================================================
 */

// Importamos axios - librería para hacer peticiones HTTP
// Similar a fetch() pero con más funcionalidades como interceptores y transformación automática de JSON
import axios from 'axios'
import { notifySessionExpired } from './sessionExpired'

/**
 * =====================================================================================
 * CREACIÓN DE LA INSTANCIA AXIOS
 * =====================================================================================
 *
 * axios.create() crea una nueva instancia de Axios con configuración personalizada.
 * Esta instancia es independiente de la global y tiene su propia configuración.
 *
 * Configuraciones importantes:
 *
 * baseURL: URL base del backend. Todas las peticiones se agregan a esta URL.
 *          Ejemplo: api.get('/facturas') → GET https://localhost:7039/api/facturas
 *
 *          process.env.VUE_APP_API_URL permite configurar diferente URL por ambiente:
 *          - Desarrollo: https://localhost:7039/api
 *          - Producción: https://api.miempresa.com/api
 *
 * timeout: Tiempo máximo de espera (en milisegundos) antes de cancelar una petición.
 *          30000ms = 30 segundos. Si el servidor no responde en 30s, la petición falla.
 * 
 * headers: Headers HTTP que se envían en todas las peticiones.
 *          'Content-Type': 'application/json' indica que enviamos/recibimos JSON
 */
const api = axios.create({
  // URL base del API - viene de variable de entorno o usa localhost por defecto
  // El operador || proporciona un valor por defecto si VUE_APP_API_URL no existe
  baseURL: process.env.VUE_APP_API_URL || 'https://localhost:7039/api',

  // Timeout de 30 segundos para peticiones
  // Si una petición tarda más, Axios la cancela y lanza un error
  timeout: 30000,

  // Headers por defecto para todas las peticiones
  headers: {
    'Content-Type': 'application/json'  // Indica que el cuerpo de las peticiones es JSON
  }
})

/**
 * =====================================================================================
 * FLAG DE CONTROL PARA REDIRECCIÓN
 * =====================================================================================
 *
 * Esta variable evita un problema común: múltiples redirecciones al login.
 *
 * Escenario del problema:
 * 1. El token JWT expira
 * 2. La página tiene 5 peticiones simultáneas (ej: cargar dashboard)
 * 3. Todas las peticiones reciben error 401 (no autorizado)
 * 4. Sin este flag, se intentarían 5 redirecciones al login
 *
 * Solución:
 * - La primera petición que falla con 401 pone el flag en true
 * - Las demás peticiones ven el flag y no intentan redirigir
 * - Solo hay UNA redirección al login
 */
let isRedirectingToLogin = false

/**
 * =====================================================================================
 * GUARD ARRANQUE SSO DESDE SMARTCARE
 * =====================================================================================
 *
 * Cuando Smartix arranca desde /auth/hub-callback?code=XXX (SSO de SmartCare),
 * el JWT que pueda haber quedado en localStorage (otro user, vencido o vacio)
 * dispara 401s en los catalogos/store/navbar que cargan en paralelo al exchange.
 * Esos 401s NO son sesion expirada legitima — son consecuencia del race del
 * arranque y el exchange los va a resolver en segundos.
 *
 * Capturamos la URL inicial AL CARGAR EL MODULO (boot del app) y mantenemos el
 * flag activo hasta que (a) llegue un 2xx que confirme que el exchange/SSO ya
 * resolvio, o (b) pasen 30s desde el boot (safety). Asi sobrevive cualquier
 * navegacion post-exchange aunque la response 401 llegue tarde.
 *
 * Esto reemplaza el fix v2 anterior (capturar __bootPath en el request
 * interceptor): el problema era que en la practica el request salia DESPUES
 * de que HubCallback ya habia navegado, asi que el __bootPath capturado ya no
 * incluia hub-callback?code= y el guard fallaba.
 */
// La ruta REAL en Smartix-FE es /auth/hub (con ?code=&returnTo=&ambiente=),
// no /auth/hub-callback. El nombre INTERNO del route es 'hub-callback' pero
// el path navegable es '/auth/hub'. El v3 chequeaba el nombre y nunca
// matcheaba en runtime — guard inoperante.
const initialBootPath = typeof window !== 'undefined'
  ? window.location.pathname + window.location.search
  : ''
const initialBootPathname = typeof window !== 'undefined'
  ? window.location.pathname
  : ''
let bootedFromHubCallback =
  (initialBootPathname === '/auth/hub' || initialBootPathname.startsWith('/auth/hub/')) &&
  initialBootPath.includes('code=')
const SSO_BOOT_SAFETY_MS = 30_000
const ssoBootStartedAt = Date.now()

function dentroDeArranqueSSO () {
  return bootedFromHubCallback &&
    (Date.now() - ssoBootStartedAt) < SSO_BOOT_SAFETY_MS
}

/**
 * =====================================================================================
 * CONFIGURACIÓN DE REINTENTOS AUTOMÁTICOS
 * =====================================================================================
 *
 * Cuando el backend se reinicia o tiene un error transitorio (500, 503, timeout),
 * las peticiones fallan inmediatamente. Sin reintentos, datos como el emisor o
 * las sucursales no se cargan y la app queda en estado incompleto.
 *
 * Esta configuración permite reintentar automáticamente peticiones fallidas
 * con un delay creciente (exponential backoff) entre intentos.
 */
const RETRY_CONFIG = {
  maxRetries: 3,
  baseDelay: 1000,  // 1s, 2s, 3s
  retryableStatuses: [500, 502, 503, 504],
  retryableMethods: ['get', 'GET']  // Solo reintentar GETs (idempotentes)
}

/**
 * =====================================================================================
 * INTERCEPTOR DE REQUEST (PETICIONES SALIENTES)
 * =====================================================================================
 *
 * Los interceptores son funciones que se ejecutan ANTES o DESPUÉS de cada petición.
 * Este interceptor se ejecuta ANTES de que la petición salga al servidor.
 *
 * ¿Para qué sirve este interceptor?
 * ---------------------------------
 * Agrega automáticamente el token JWT a cada petición.
 * Así no tenemos que escribir: headers: { Authorization: 'Bearer xxx' } en cada llamada.
 *
 * FLUJO:
 * 1. Un servicio llama api.get('/facturas')
 * 2. ANTES de enviar, este interceptor se ejecuta
 * 3. Busca el token en localStorage
 * 4. Si existe, lo agrega al header Authorization
 * 5. La petición sale con el token incluido
 *
 * FORMATO DEL TOKEN (Bearer Token):
 * ---------------------------------
 * Authorization: Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9...
 *
 * "Bearer" es el esquema de autenticación que indica que estamos usando tokens JWT.
 * El backend espera este formato exacto para validar la autenticación.
 *
 * RELACIÓN CON auth.service.js:
 * -----------------------------
 * - auth.service.js guarda el token en localStorage después del login exitoso
 * - Este interceptor lee ese token y lo usa en cada petición
 */
api.interceptors.request.use(
  // Función que se ejecuta cuando la petición está lista para salir
  (config) => {
    // Intentar obtener el token del almacenamiento local del navegador
    // localStorage persiste incluso si el usuario cierra el navegador
    const token = localStorage.getItem('dte_token')

    // Si hay un token guardado, agregarlo al header de la petición
    if (token) {
      // config.headers contiene todos los headers HTTP de esta petición
      // Authorization es el header estándar para enviar credenciales
      config.headers.Authorization = `Bearer ${token}`
    }

    // Propagar el ambiente elegido al login como header en cada request.
    // El BE (AmbienteResolver) prefiere este valor sobre emisor.CatAmbienteDestinoId
    // de BD para decidir a qué API de MH enviar (Pruebas vs Producción) y qué
    // credenciales/llaves usar. Sin esto, si el user selecciona Pruebas pero el
    // emisor en BD quedó en Producción (o viceversa), la emisión terminaba en
    // el ambiente equivocado. '00' = Pruebas, '01' = Producción.
    const ambiente = localStorage.getItem('dte_ambiente')
    if (ambiente) {
      config.headers['X-Ambiente'] = ambiente
    }

    // IMPORTANTE: Siempre debemos retornar config para que la petición continúe
    return config
  },

  // Función que se ejecuta si hay un error al preparar la petición (raro, pero posible)
  (error) => {
    // Rechazamos la promesa para que el código que llamó pueda manejar el error
    return Promise.reject(error)
  }
)

/**
 * =====================================================================================
 * INTERCEPTOR DE RESPONSE (RESPUESTAS ENTRANTES)
 * =====================================================================================
 *
 * Este interceptor se ejecuta DESPUÉS de recibir una respuesta del servidor.
 * Su propósito principal es manejar errores de forma centralizada.
 *
 * ¿Por qué manejar errores aquí?
 * ------------------------------
 * En lugar de verificar errores en cada componente/servicio, lo hacemos una vez aquí.
 * Esto es especialmente útil para:
 * - Error 401: Token expirado → redirigir al login automáticamente
 * - Error 403: Sin permisos → mostrar mensaje apropiado
 * - Error 500: Error del servidor → loguear para debugging
 *
 * CÓDIGOS HTTP COMUNES:
 * ---------------------
 * 200-299: Éxito (OK, Created, No Content)
 * 400: Bad Request - datos inválidos enviados
 * 401: Unauthorized - no autenticado o token expirado
 * 403: Forbidden - autenticado pero sin permisos
 * 404: Not Found - recurso no existe
 * 500: Internal Server Error - error del servidor
 *
 * FLUJO DEL INTERCEPTOR:
 * ----------------------
 * Respuesta del servidor
 *        ↓
 * ¿Fue exitosa (2xx)?
 *    SÍ → Pasar la respuesta al servicio que la llamó
 *    NO → Analizar código de error y actuar según corresponda
 */
api.interceptors.response.use(
  // Función para respuestas exitosas (código 2xx)
  // Resetear flag de redirección cuando hay respuestas exitosas
  (response) => {
    isRedirectingToLogin = false
    // Bug v3/v4: limpiar el flag en CUALQUIER 2xx tiene race condition
    // — los catalogos/store cargan en paralelo, system-logo 200 llega
    // ANTES de mi-perfil 401 → flag limpio → modal aparece igual.
    // Fix v5: limpiar SOLO cuando llega el 2xx del exchange en si
    // (/api/Auth/hub-login). Sino el flag queda hasta el safety de 30s.
    const url = response.config?.url || ''
    if (url.includes('/Auth/hub-login') || url.includes('/auth/hub-login')) {
      bootedFromHubCallback = false
    }
    return response
  },

  // Función para respuestas con error (cualquier código fuera de 2xx)
  async (error) => {
    const config = error.config

    // Reintentar automáticamente para errores transitorios (backend reiniciándose, etc.)
    if (config && !config.__retryCount) {
      config.__retryCount = 0
    }

    const isRetryable = config &&
      RETRY_CONFIG.retryableMethods.includes(config.method) &&
      config.__retryCount < RETRY_CONFIG.maxRetries &&
      (
        // Error de red (servidor no disponible, timeout)
        !error.response ||
        // Errores de servidor transitorios
        RETRY_CONFIG.retryableStatuses.includes(error.response.status)
      )

    if (isRetryable) {
      config.__retryCount++
      const delay = RETRY_CONFIG.baseDelay * config.__retryCount
      console.warn(`[API] Reintento ${config.__retryCount}/${RETRY_CONFIG.maxRetries} para ${config.url} en ${delay}ms`)
      await new Promise(resolve => setTimeout(resolve, delay))
      return api(config)
    }

    // error.response existe si el servidor respondió (aunque con error)
    // Si error.response es undefined, significa que la petición nunca llegó al servidor
    if (error.response) {
      // Obtenemos la URL de la petición que falló (útil para debugging)
      // El operador ?. (optional chaining) evita errores si config es undefined
      const url = error.config?.url || ''

      // Verificamos si la petición fue hacia endpoints de autenticación
      // Esto es importante para NO redirigir al login si el login mismo falló
      const isAuthEndpoint = url.includes('/Auth/')

      // Estamos en el arranque SSO desde SmartCare?
      // bootedFromHubCallback se setea al CARGAR EL MODULO si la URL inicial
      // era /auth/hub-callback?code=. Lo limpiamos en la primera 2xx (el
      // exchange exitoso confirma que el SSO ya resolvio) o tras 30s safety.
      // Asi sobrevive cualquier navegacion post-exchange aunque la response
      // 401 llegue despues de que HubCallback navego a /emision/...
      const enArranqueSSO = dentroDeArranqueSSO()

      // Analizamos el código de estado HTTP para decidir qué hacer
      switch (error.response.status) {

        /**
         * ERROR 401 - NO AUTORIZADO
         * -------------------------
         * Significa que el usuario no está autenticado o su token expiró.
         *
         * Dos escenarios posibles:
         * 1. Login fallido: usuario puso mal su contraseña
         *    → NO redirigir, dejar que el formulario de login muestre el error
         *
         * 2. Token expirado: usuario estaba navegando y su sesión caducó
         *    → SÍ redirigir al login para que vuelva a autenticarse
         *
         * El flag isRedirectingToLogin evita múltiples redirecciones si hay
         * varias peticiones fallando simultáneamente.
         */
        case 401:
          // Sesion expirada (token vencido o TokenVersion bumpeado desde otra app).
          // En vez de limpiar storage y redirigir en silencio, abrimos un dialog
          // que avise al usuario y le ofrezca el boton "Volver a iniciar sesion".
          // Skip si es el endpoint de login (credenciales malas) o si ya estamos
          // en /login (no hay sesion que avisar). Tambien skip si es el arranque
          // SSO desde SmartCare (isHubCallbackWithCode) para no interrumpir el
          // intercambio del ?code= por el JWT real.
          if (!isAuthEndpoint && !isRedirectingToLogin
              && !window.location.pathname.includes('/login')
              && !enArranqueSSO) {
            isRedirectingToLogin = true
            // Preservar la URL actual (path + query) para volver al re-SSO. Caso
            // tipico: SmartCare abrio Smartix con ?prefillId=... y Smartix tenia
            // JWT viejo por cambio de org. Sin esto, el user se quedaba en /dashboard
            // tras re-loguear y perdia el contexto de la factura que iba a emitir.
            // No guardamos rutas de auth/callback ni la raiz.
            const currentPath = window.location.pathname + window.location.search
            if (!currentPath.startsWith('/auth/') && currentPath !== '/' && currentPath !== '/dashboard') {
              try { localStorage.setItem('dte_return_to', currentPath) } catch (e) { /* sessionStorage lleno o bloqueado */ }
            }
            notifySessionExpired()
          }
          break

        /**
         * ERROR 403 - PROHIBIDO/ACCESO DENEGADO
         * -------------------------------------
         * El usuario está autenticado pero no tiene permisos para este recurso.
         * Por ejemplo: un usuario normal intentando acceder a funciones de admin.
         * Solo logueamos el error; el componente que hizo la petición puede
         * mostrar un mensaje apropiado al usuario.
         */
        case 403:
          console.error('Acceso denegado')
          break

        /**
         * ERROR 404 - NO ENCONTRADO
         * -------------------------
         * El recurso solicitado no existe.
         * Ejemplos: factura con ID que no existe, endpoint mal escrito, etc.
         */
        case 404:
          console.error('Recurso no encontrado')
          break

        /**
         * ERROR 500 - ERROR INTERNO DEL SERVIDOR
         * --------------------------------------
         * Algo falló en el backend. No es problema del frontend.
         * Logueamos para que los desarrolladores puedan investigar.
         */
        case 500:
          console.error('Error interno del servidor')
          break

        /**
         * CUALQUIER OTRO ERROR
         * --------------------
         * Otros códigos de error (400, 422, etc.)
         * Logueamos los detalles de la respuesta para debugging.
         */
        default:
          console.error('Error en la solicitud:', error.response.data)
      }
    }
    // Si error.request existe pero error.response no, significa que:
    // - La petición se envió pero nunca llegó respuesta
    // - Posibles causas: servidor caído, sin conexión a internet, timeout
    else if (error.request) {
      console.error('No se pudo conectar con el servidor')
    }

    // IMPORTANTE: Siempre rechazamos la promesa para que el código que llamó
    // pueda manejar el error con try/catch o .catch()
    // Si no rechazáramos, el código que llamó pensaría que la petición fue exitosa
    return Promise.reject(error)
  }
)

/**
 * =====================================================================================
 * EXPORTACIÓN DE LA INSTANCIA
 * =====================================================================================
 *
 * Exportamos la instancia configurada de Axios para que otros archivos la importen.
 *
 * USO EN OTROS ARCHIVOS:
 * ----------------------
 * import api from './api'
 *
 * // Petición GET (obtener datos)
 * const response = await api.get('/facturas')
 *
 * // Petición POST (crear datos)
 * const response = await api.post('/facturas', datosFactura)
 *
 * // Petición PUT (actualizar datos completos)
 * const response = await api.put('/facturas/123', datosActualizados)
 *
 * // Petición DELETE (eliminar datos)
 * await api.delete('/facturas/123')
 *
 * ARCHIVOS QUE USAN ESTE MÓDULO:
 * ------------------------------
 * - src/services/auth.service.js → Login y autenticación
 * - src/services/facturas.service.js → Gestión de facturas
 * - src/services/catalogos.service.js → Catálogos de Hacienda
 * - src/services/emisores.service.js → Gestión de emisores
 * - src/services/receptores.service.js → Gestión de receptores/clientes
 * - src/services/productosServicios.service.js → Productos y servicios
 * - src/services/sucursales.service.js → Sucursales del emisor
 */
export default api
